import { apiFetch } from '../lib/apiFetch';

export const SEARCH_KINDS = ['venue', 'booking', 'privateEvent'] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];

export interface UpcomingWorkshopHit {
  id: string;
  name: string | null;
  /** Venue wall-clock time with no trailing `Z` — format with `utils/venueTime`. */
  localStartsAt: string | null;
  seatsLeft: number;
}

export interface VenueHit {
  id: string;
  name: string;
  city: string;
  state: string;
  isActive: boolean;
  /** Filled for the top venue only. */
  upcomingWorkshops: UpcomingWorkshopHit[];
}

export interface BookingHit {
  id: string;
  displayRef: string | null;
  customerName: string | null;
  workshopName: string | null;
  localStartsAt: string | null;
  venueName: string;
}

export interface PrivateEventHit {
  id: string;
  clientName: string;
  contactName: string | null;
  localStartsAt: string | null;
  locationName: string | null;
}

interface SearchGroup<Kind extends SearchKind, Item> {
  kind: Kind;
  total: number;
  items: Item[];
}

export type SearchResultGroup =
  | SearchGroup<'venue', VenueHit>
  | SearchGroup<'booking', BookingHit>
  | SearchGroup<'privateEvent', PrivateEventHit>;

export interface SearchResponse {
  groups: SearchResultGroup[];
}

export interface SearchParams {
  q: string;
  type?: SearchKind;
  limit?: number;
}

/** Carries the HTTP status so the palette can tell a refusal from an outage. */
export class SearchFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'SearchFetchError';
  }
}

export async function fetchSearch(
  params: SearchParams,
  opts: { signal?: AbortSignal } = {},
): Promise<SearchResponse> {
  const query = new URLSearchParams({ q: params.q });
  if (params.type) query.set('type', params.type);
  if (params.limit) query.set('limit', String(params.limit));
  const res = await apiFetch(`/api/v1/search?${query.toString()}`, { signal: opts.signal });
  if (!res.ok) throw new SearchFetchError(`Search failed: ${res.status}`, res.status);
  return (await res.json()) as SearchResponse;
}
